import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@f2e/ui/components/base"

import { HistoryDetail } from "@/components/History"
import { useToast } from "@/hooks"
import { useGetHistoryApi, historyResponseToHistory } from "@/hooks/api"
import { useDeleteHistoryApi, useUpdateHistoryApi } from "@/hooks/api/histories"
import { FetchStatus } from "@/hooks/useFetch"
import {
  getApiErrorPayloadMessage,
  getUserFacingApiErrorMessage,
} from "@/lib/apiErrors"
import type { History, Workspace } from "@/types"

import { ActionButton } from "../HistoryList/components/ActionButton"

export interface ThreadSessionChange {
  action: "updated" | "deleted"
  historyId: number
}

export interface ThreadSessionSheetProps {
  historyId: number | null
  workspace: Workspace
  deleteBlocked: boolean
  onOpenChange: (open: boolean) => void
  onDeletePendingChange: (pending: boolean) => void
  onSessionChanged: (change: ThreadSessionChange) => void
}

const ThreadSessionSheet = ({
  historyId,
  workspace,
  deleteBlocked,
  onOpenChange,
  onDeletePendingChange,
  onSessionChanged,
}: ThreadSessionSheetProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [detailVersion, setDetailVersion] = useState(0)
  const [deletingHistoryId, setDeletingHistoryId] = useState<number | null>(
    null
  )
  const currentHistoryIdRef = useRef(historyId)
  currentHistoryIdRef.current = historyId
  const {
    data,
    status,
    fetchFn: getHistory,
  } = useGetHistoryApi(historyId ?? -1)
  const {
    data: updateResponse,
    error: updateError,
    fetchFn: updateHistory,
  } = useUpdateHistoryApi(historyId ?? -1)
  const { fetchFn: deleteHistory } = useDeleteHistoryApi(
    deletingHistoryId ?? historyId ?? -1
  )
  const history = useMemo(
    () => (data?.data ? historyResponseToHistory(data.data) : undefined),
    [data]
  )

  useEffect(() => {
    if (historyId === null) return
    getHistory()
  }, [getHistory, historyId, detailVersion])

  useEffect(() => {
    if (!updateError) return

    alertToast({
      title: t("historyList.message.updateFailed"),
      description: getUserFacingApiErrorMessage(
        updateError,
        t,
        updateResponse?.message || t("historyList.message.updateFailed")
      ),
    })
  }, [alertToast, t, updateError, updateResponse])

  const handleUpdated = (updatedHistory: History) => {
    updateHistory({
      jsonBody: { name: updatedHistory.name },
      callbackFn: (response) => {
        if (!response?.data) return

        setDetailVersion((value) => value + 1)
        onSessionChanged({ action: "updated", historyId: updatedHistory.id })
      },
    })
  }

  const handleDeleted = (deletedHistory: History) => {
    if (deleteBlocked || deletingHistoryId !== null) return
    setDeletingHistoryId(deletedHistory.id)
    onDeletePendingChange(true)

    void deleteHistory()
      .then((response) => {
        if (!response || response.error_code !== 0) {
          if (response) {
            alertToast({
              title: t("historyList.message.deleteFailed"),
              description: getApiErrorPayloadMessage(
                response,
                t,
                t("historyList.message.deleteFailed")
              ),
            })
          }
          return
        }

        successToast({
          title: t("historyList.message.deleted"),
          description: t("historyList.message.deletedDesc", {
            name: deletedHistory.name,
          }),
        })
        if (currentHistoryIdRef.current === deletedHistory.id) {
          onOpenChange(false)
        }
        onSessionChanged({ action: "deleted", historyId: deletedHistory.id })
      })
      .catch((error: unknown) => {
        alertToast({
          title: t("historyList.message.deleteFailed"),
          description: getUserFacingApiErrorMessage(
            error,
            t,
            t("historyList.message.deleteFailed")
          ),
        })
      })
      .finally(() => {
        setDeletingHistoryId(null)
        onDeletePendingChange(false)
      })
  }

  return (
    <Sheet open={historyId !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-[min(92vw,760px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none [&>button:last-child]:z-30"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{t("historyList.thread.sessionDetail")}</SheetTitle>
        </SheetHeader>
        {historyId !== null && status === FetchStatus.Error && (
          <div className="flex h-full items-center justify-center px-6 text-sm text-muted-foreground">
            {t("historyList.thread.sessionLoadFailed")}
          </div>
        )}
        {historyId !== null && !history && status !== FetchStatus.Error && (
          <div className="flex h-full items-center justify-center px-6 text-sm text-muted-foreground">
            {t("historyList.thread.loadingSession")}
          </div>
        )}
        {historyId !== null && history && (
          <div
            className="h-full min-h-0 overflow-y-auto"
            data-scroll-container="history-detail"
          >
            <HistoryDetail
              key={`${historyId}:${detailVersion}`}
              historyId={historyId}
              mode="embedded"
              readOnly={true}
              headerActions={
                !deleteBlocked && deletingHistoryId === null ? (
                  <ActionButton
                    history={history}
                    workspace={workspace}
                    onEdit={handleUpdated}
                    onDelete={handleDeleted}
                    sessionScoped={true}
                  />
                ) : undefined
              }
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default ThreadSessionSheet
